// Step1SportType — ActivityWizard Steg 1 (delspår 3b): sport + aktivitetstyp + synlighet.
//
// Datadriven sport-väljare (operatör 2026-05-28, Lesson 48): the club's active sports
// are derived from courts.sport_type (distinct per org, via useOrgHall — Lesson 38 reuse;
// sport_policies is empty per Fas 0 so courts is the source of truth). When the club has
// ≤1 sport the selector is hidden and sport_type is set implicitly (SET_SPORT, non-dirty);
// with 2+ sports a grid of only the active sports renders. No disabled-padding of
// unsupported sports — the selector appears automatically when a club gains a 2nd sport.
//
// player_can_create filtered in consumption (Lesson 38: shared hook untouched). All user
// selections dispatch UPDATE_FORM; the implicit single-sport set uses SET_SPORT to avoid
// phantom-dirty (D4 / Lesson D2).

import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useActivityWizard } from "../ActivityWizardContext";
import { useActivityTypes, type ActivityType } from "@/hooks/useActivityTypes";
import { useOrgHall } from "@/hooks/useOrgHall";
import { SPORT_OPTIONS } from "@/lib/sports";
import SportPill from "../SportPill";
import ActivityTypeCard from "../ActivityTypeCard";
import WizardStepHero from "../WizardStepHero";
import { cn } from "@/lib/utils";
import { Globe, Lock } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const FALLBACK_SPORT = "padel";

export default function Step1SportType() {
  const { t, i18n } = useTranslation("admin");
  const { state, dispatch } = useActivityWizard();
  const { types, isLoading: typesLoading } = useActivityTypes();
  const { courts, isLoading: courtsLoading } = useOrgHall();
  const [infoType, setInfoType] = useState<ActivityType | null>(null);

  const isEn = i18n.language?.startsWith("en");

  // Distinct active sports for the club (from courts.sport_type).
  const activeSports = useMemo(() => {
    const set = new Set<string>();
    for (const c of courts) {
      const s = (c as { sport_type?: string | null }).sport_type;
      if (s) set.add(s);
    }
    return [...set].sort();
  }, [courts]);

  const showSportSelector = !courtsLoading && activeSports.length >= 2;
  const effectiveSport = activeSports[0] ?? FALLBACK_SPORT;
  const selectedSport = state.formData.sport_type ?? FALLBACK_SPORT;
  const selectedTypeId = state.formData.activityTypeId ?? null;
  const visibility = state.formData.visibility ?? "public";

  // Keep sport_type a valid active sport without marking dirty (system-init, not
  // user input). No-op for all current single-padel clubs (initialState already padel).
  useEffect(() => {
    if (courtsLoading) return;
    if (activeSports.length >= 1 && !activeSports.includes(selectedSport)) {
      dispatch({ type: "SET_SPORT", payload: effectiveSport });
    }
  }, [courtsLoading, activeSports, selectedSport, effectiveSport, dispatch]);

  // Wizard = player tool → only player-creatable types (beslut #6).
  const playerTypes = types.filter((tp) => tp.player_can_create);

  const handleSelectSport = (sport: string) =>
    dispatch({ type: "UPDATE_FORM", payload: { sport_type: sport } });
  const handleSelectType = (id: string) =>
    dispatch({ type: "UPDATE_FORM", payload: { activityTypeId: id } });
  const handleVisibility = (value: "public" | "private") =>
    dispatch({ type: "UPDATE_FORM", payload: { visibility: value } });

  const infoName = infoType ? ((isEn ? infoType.name_en : null) ?? infoType.name) : "";
  const infoBody = infoType ? ((isEn ? infoType.description_en : null) ?? infoType.description) : "";

  return (
    <div className="space-y-4 md:space-y-6">
      <WizardStepHero
        title={t("activityWizard.step1.heroTitle", { defaultValue: "Vad vill du spela?" })}
        subtitle={t("activityWizard.step1.heroSubtitle", {
          defaultValue: "Välj typ av aktivitet. Du kan bjuda in spelare i ett senare steg.",
        })}
      />
      {/* Sport-pill-grid — rendered only when the club has 2+ active sports (Lesson 48). */}
      {showSportSelector && (
        <section>
          <h2 className="mb-2 text-sm font-medium text-muted-foreground">
            {t("activityWizard.step1.sportHeading", { defaultValue: "Välj sport" })}
          </h2>
          <div className="grid grid-cols-3 gap-2">
            {activeSports.map((slug) => {
              const opt = SPORT_OPTIONS.find((o) => o.value === slug);
              return (
                <SportPill
                  key={slug}
                  sport={slug}
                  label={opt?.label ?? slug}
                  selected={selectedSport === slug}
                  onSelect={handleSelectSport}
                />
              );
            })}
          </div>
        </section>
      )}

      {/* Aktivitetstyp-kort */}
      <section>
        <h2 className="mb-2 text-sm font-medium text-muted-foreground">
          {t("activityWizard.step1.typeHeading", { defaultValue: "Välj aktivitet" })}
        </h2>
        {typesLoading ? (
          <div className="grid grid-cols-2 gap-2 md:grid-cols-3 md:gap-3">
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                className="min-h-[140px] animate-pulse rounded-xl border border-border bg-muted/40"
              />
            ))}
          </div>
        ) : playerTypes.length === 0 ? (
          <p
            data-testid="activity-types-empty"
            className="rounded-md border border-dashed border-border bg-muted/30 p-6 text-center text-sm text-muted-foreground"
          >
            {t("activityWizard.step1.emptyState", { defaultValue: "Inga aktivitetstyper konfigurerade" })}
          </p>
        ) : (
          <div className="grid grid-cols-2 gap-2 md:grid-cols-3 md:gap-3">
            {playerTypes.map((tp) => (
              <ActivityTypeCard
                key={tp.id}
                type={tp}
                selected={selectedTypeId === tp.id}
                onSelect={handleSelectType}
                onInfoClick={setInfoType}
              />
            ))}
          </div>
        )}
      </section>

      {/* Synlighets-toggle (beslut #7: Öppen/Privat, default public) */}
      <section>
        <h2 className="mb-2 text-sm font-medium text-muted-foreground">
          {t("activityWizard.step1.visibilityHeading", { defaultValue: "Synlighet" })}
        </h2>
        {/* Livfull: segmented control (neutral bed, selected side lifts to a card surface). */}
        <div className="inline-flex w-full gap-1 rounded-[14px] bg-muted p-1">
          {(["public", "private"] as const).map((v) => (
            <button
              key={v}
              type="button"
              aria-pressed={visibility === v}
              data-testid={`visibility-${v}`}
              onClick={() => handleVisibility(v)}
              className={cn(
                "flex flex-1 items-center justify-center gap-1.5 rounded-[10px] px-3 py-2 text-sm font-medium transition-colors",
                visibility === v
                  ? "bg-card text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              {v === "public" ? <Globe className="h-4 w-4" /> : <Lock className="h-4 w-4" />}
              {v === "public"
                ? t("activityWizard.step1.visibilityPublic", { defaultValue: "Öppen" })
                : t("activityWizard.step1.visibilityPrivate", { defaultValue: "Privat" })}
            </button>
          ))}
        </div>
      </section>

      {/* Info-dialog — visas bara om en typ har description (beslut #5) */}
      <AlertDialog open={!!infoType} onOpenChange={(open) => !open && setInfoType(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{infoName}</AlertDialogTitle>
            <AlertDialogDescription>{infoBody}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={() => setInfoType(null)}>
              {t("activityWizard.step1.infoClose", { defaultValue: "Stäng" })}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
